/**
 * /early-access — bilingual public waitlist signup page.
 *
 * Scope (Early Access / Waitlist v1):
 *   * Single-page signup form with explicit Double Opt-In consent.
 *   * Honeypot (``website``) + explicit marketing-consent checkbox.
 *   * Response path is always a generic "check your inbox" success —
 *     the backend returns ``{ok: true}`` for every outcome so the
 *     endpoint cannot be used to enumerate subscribers.
 *   * Emits three privacy-safe PostHog funnel events (allowlisted
 *     in ``lib/analytics-allowlist.js``) and NEVER sends an email or
 *     token to analytics:
 *       early_access_viewed            — on mount
 *       early_access_signup_submitted  — after a successful POST
 *   * ``/early-access?confirmed=1`` renders a dedicated confirmation
 *     success state and fires ``early_access_doi_confirmed`` once.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mail, Gauge, Target, ShieldCheck, Sparkles, CheckCircle2, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useTranslation } from "@/lib/i18n";
import api from "@/lib/api";
import { capture } from "@/lib/analytics";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function readSource(search) {
  try {
    const sp = new URLSearchParams(search || "");
    const src = sp.get("source");
    if (!src) return "early_access";
    const clean = String(src).replace(/[^A-Za-z0-9._-]/g, "").slice(0, 64);
    return clean || "early_access";
  } catch { return "early_access"; }
}

function readUtm(search) {
  try {
    const sp = new URLSearchParams(search || "");
    const safe = (v) => {
      if (!v) return undefined;
      const c = String(v).replace(/[^A-Za-z0-9._-]/g, "").slice(0, 64).toLowerCase();
      return c || undefined;
    };
    return {
      utm_source: safe(sp.get("utm_source")),
      utm_medium: safe(sp.get("utm_medium")),
      utm_campaign: safe(sp.get("utm_campaign")),
    };
  } catch { return {}; }
}

export default function EarlyAccess() {
  const { t, locale, setLocale } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const confirmed = searchParams.get("confirmed") === "1";
  const alreadyConfirmed = searchParams.get("already") === "1";

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [emailErr, setEmailErr] = useState("");
  const [consentErr, setConsentErr] = useState("");
  const [genericErr, setGenericErr] = useState("");

  // Fire the funnel-view event exactly once on mount.
  const viewedRef = useRef(false);
  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    try {
      capture("early_access_viewed", { locale, source: readSource(location.search) });
    } catch { /* noop */ }
  }, [locale, location.search]);

  // DOI confirmed landing — fire the confirmed event once.
  const confirmedFiredRef = useRef(false);
  useEffect(() => {
    if (!confirmed || confirmedFiredRef.current) return;
    confirmedFiredRef.current = true;
    try {
      capture("early_access_doi_confirmed", { locale });
    } catch { /* noop */ }
  }, [confirmed, locale]);

  // Document SEO + Open Graph + Twitter metadata.
  //
  // All tags are route-scoped: the cleanup restores previous values
  // (or removes freshly-created tags) so SPA navigation away from
  // /early-access does NOT leave Early-Access metadata attached to
  // unrelated routes.  Canonical and og:url point to the production
  // host; robots stays noindex,nofollow in every non-production
  // environment (preview + staging + development).
  useEffect(() => {
    const CANONICAL_URL = "https://stockaily.ai/early-access";
    const title = t("early_access.seo_title");
    const description = t("early_access.seo_description");
    const ogTitle = t("early_access.og_title");
    const ogDescription = t("early_access.og_description");

    const prevTitle = document.title;
    document.title = title;

    // Resolve environment from the runtime-config (set at container
    // build time via PUBLIC_PRELAUNCH_MODE's sibling APP_ENVIRONMENT).
    let appEnv = "";
    try {
      const rc = (typeof window !== "undefined") ? window.__STOCKAILY_RUNTIME_CONFIG__ : null;
      appEnv = (rc && typeof rc.appEnvironment === "string") ? rc.appEnvironment : "";
    } catch { /* noop */ }
    const robotsValue = appEnv === "production" ? "index,follow" : "noindex,nofollow";

    // Snapshot every tag we may touch, so cleanup is deterministic.
    const touched = [];
    const upsertMeta = (selector, attrName, attrValue, contentKey, contentValue) => {
      let el = document.head.querySelector(selector);
      const created = !el;
      let prevContent = null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      } else {
        prevContent = el.getAttribute(contentKey);
      }
      el.setAttribute(contentKey, contentValue);
      touched.push({ el, created, prevContent, contentKey });
    };

    upsertMeta('meta[name="description"]', "name", "description", "content", description);
    upsertMeta('meta[name="robots"]', "name", "robots", "content", robotsValue);
    upsertMeta('meta[property="og:title"]', "property", "og:title", "content", ogTitle);
    upsertMeta('meta[property="og:description"]', "property", "og:description", "content", ogDescription);
    upsertMeta('meta[property="og:url"]', "property", "og:url", "content", CANONICAL_URL);
    upsertMeta('meta[property="og:type"]', "property", "og:type", "content", "website");
    upsertMeta('meta[name="twitter:card"]', "name", "twitter:card", "content", "summary");
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", "content", ogTitle);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", "content", ogDescription);

    // Canonical link element.
    let canonical = document.head.querySelector('link[rel="canonical"]');
    const canonicalCreated = !canonical;
    const canonicalPrev = canonical ? canonical.getAttribute("href") : null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", CANONICAL_URL);

    return () => {
      document.title = prevTitle;
      for (const t of touched) {
        if (t.created) {
          if (t.el && t.el.parentNode) t.el.parentNode.removeChild(t.el);
        } else if (t.prevContent != null) {
          t.el.setAttribute(t.contentKey, t.prevContent);
        }
      }
      if (canonicalCreated) {
        if (canonical && canonical.parentNode) canonical.parentNode.removeChild(canonical);
      } else if (canonicalPrev != null) {
        canonical.setAttribute("href", canonicalPrev);
      }
    };
  }, [t, locale]);

  const attribution = useMemo(() => ({
    source: readSource(location.search),
    referrer: typeof document !== "undefined" ? (document.referrer || "") : "",
    ...readUtm(location.search),
  }), [location.search]);

  const onSubmit = useCallback(async (e) => {
    e.preventDefault();
    setEmailErr("");
    setConsentErr("");
    setGenericErr("");
    const trimmedEmail = (email || "").trim();
    if (!trimmedEmail) {
      setEmailErr(t("early_access.form_err_email_required"));
      return;
    }
    if (!EMAIL_RE.test(trimmedEmail)) {
      setEmailErr(t("early_access.form_err_email_invalid"));
      return;
    }
    if (!consent) {
      setConsentErr(t("early_access.form_err_consent_required"));
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/waitlist/signup", {
        email: trimmedEmail,
        first_name: (firstName || "").trim() || null,
        locale,
        marketing_opt_in: true,
        source: attribution.source,
        referrer: attribution.referrer || null,
        utm_source: attribution.utm_source || null,
        utm_medium: attribution.utm_medium || null,
        utm_campaign: attribution.utm_campaign || null,
        website, // honeypot (will be empty for real humans)
      });
      setSubmitted(true);
      try {
        capture("early_access_signup_submitted", { locale, source: attribution.source });
      } catch { /* noop */ }
    } catch (err) {
      setGenericErr(t("early_access.form_err_generic"));
    } finally {
      setSubmitting(false);
    }
  }, [email, firstName, consent, website, locale, attribution, t]);

  const privacyHref = locale === "de" ? "/de/datenschutz" : "/en/privacy";
  const imprintHref = locale === "de" ? "/de/impressum" : "/en/legal-notice";
  const disclaimerHref = locale === "de" ? "/de/disclaimer" : "/en/disclaimer";
  const methodologyHref = "/research-transparency";

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground" data-testid="early-access-page">
      {/* Top bar — minimal, mirrors the pre-launch holding feel. */}
      <header className="w-full border-b border-border/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link to="/" className="font-display text-base sm:text-lg tracking-tight" data-testid="early-access-brand">
            Stockaily
          </Link>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setLocale("en")}
              className={`text-xs px-2 py-1 rounded-full border ${locale === "en" ? "bg-foreground text-background border-transparent" : "border-border/70 text-muted-foreground hover:text-foreground"}`}
              data-testid="early-access-lang-en"
              aria-pressed={locale === "en"}
            >EN</button>
            <button
              type="button"
              onClick={() => setLocale("de")}
              className={`text-xs px-2 py-1 rounded-full border ${locale === "de" ? "bg-foreground text-background border-transparent" : "border-border/70 text-muted-foreground hover:text-foreground"}`}
              data-testid="early-access-lang-de"
              aria-pressed={locale === "de"}
            >DE</button>
          </div>
        </div>
      </header>

      <main className="flex-1 relative overflow-hidden">
        {/* Ambient background accent */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute -top-24 -left-24 w-[560px] h-[560px] rounded-full blur-3xl opacity-40 bg-gradient-to-br from-amber-200/40 via-rose-200/20 to-transparent dark:from-amber-500/10 dark:via-rose-500/5" />
          <div className="absolute -bottom-24 -right-24 w-[560px] h-[560px] rounded-full blur-3xl opacity-30 bg-gradient-to-tl from-sky-200/40 via-emerald-200/20 to-transparent dark:from-sky-500/10 dark:via-emerald-500/5" />
        </div>

        <section className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-14 pb-12 sm:pb-20 grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-14 items-start">
          {/* Left — pitch */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="lg:col-span-3"
          >
            <span className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card/60 px-2.5 py-1 text-[11px] uppercase tracking-wider text-muted-foreground" data-testid="early-access-eyebrow">
              <Sparkles className="w-3 h-3" /> {t("early_access.hero_eyebrow")}
            </span>
            <h1
              className="font-display mt-3 text-[32px] leading-[1.05] sm:text-5xl lg:text-6xl font-semibold tracking-tight text-balance"
              data-testid="early-access-hero-title"
            >
              {t("early_access.hero_title")}
            </h1>
            <p
              className="mt-3 sm:mt-4 text-sm sm:text-base text-muted-foreground max-w-[52ch]"
              data-testid="early-access-hero-subtitle"
            >
              {t("early_access.hero_subtitle")}
            </p>

            <ul className="mt-6 sm:mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-testid="early-access-benefits">
              <BenefitCard
                icon={<Gauge className="w-4 h-4" />}
                title={t("early_access.benefit_opp_title")}
                body={t("early_access.benefit_opp_body")}
                testId="early-access-benefit-opp"
              />
              <BenefitCard
                icon={<Target className="w-4 h-4" />}
                title={t("early_access.benefit_fv_title")}
                body={t("early_access.benefit_fv_body")}
                testId="early-access-benefit-fv"
              />
              <BenefitCard
                icon={<ShieldCheck className="w-4 h-4" />}
                title={t("early_access.benefit_risk_title")}
                body={t("early_access.benefit_risk_body")}
                testId="early-access-benefit-risk"
              />
            </ul>

            <div className="mt-6 sm:mt-8 rounded-2xl border border-border/70 bg-card/40 p-4 sm:p-5">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
                {t("early_access.preview_label")}
              </div>
              <div
                className="flex gap-3 overflow-x-auto overscroll-x-contain snap-x snap-mandatory pb-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:overflow-visible sm:pb-0"
                data-testid="early-access-preview-slider"
                aria-label={t("early_access.preview_label")}
              >
                <PreviewTile label={t("early_access.preview_opp")} value={t("early_access.preview_opp_value")} tone="emerald" />
                <PreviewTile label={t("early_access.preview_fv")} value={t("early_access.preview_fv_value")} tone="sky" />
                <PreviewTile label={t("early_access.preview_risk")} value={t("early_access.preview_risk_value")} tone="amber" />
              </div>
              <p className="mt-3 text-[11px] text-muted-foreground">{t("early_access.preview_note")}</p>
            </div>
          </motion.div>

          {/* Right — the signup card */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            className="lg:col-span-2 lg:sticky lg:top-6"
          >
            <div className="rounded-2xl border border-border/80 bg-card/80 backdrop-blur-md shadow-lg p-5 sm:p-6" data-testid="early-access-card">
              {(confirmed || alreadyConfirmed) ? (
                <ConfirmedState
                  alreadyConfirmed={alreadyConfirmed}
                  t={t}
                />
              ) : submitted ? (
                <SuccessState t={t} />
              ) : (
                <>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{t("early_access.form_eyebrow")}</div>
                  <h2 className="font-display mt-1.5 text-xl sm:text-2xl font-semibold tracking-tight" data-testid="early-access-form-title">
                    {t("early_access.form_title")}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">{t("early_access.form_subtitle")}</p>

                  <form onSubmit={onSubmit} className="mt-5 space-y-4" noValidate data-testid="early-access-form">
                    {/* Honeypot — hidden from humans + assistive tech, submitted by bots. */}
                    <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
                      <label htmlFor="ea-website">Website</label>
                      <input
                        id="ea-website"
                        name="website"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={website}
                        onChange={(e) => setWebsite(e.target.value)}
                        data-testid="early-access-honeypot"
                      />
                    </div>

                    <div>
                      <Label htmlFor="ea-email" className="text-xs">{t("early_access.form_email_label")}</Label>
                      <Input
                        id="ea-email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t("early_access.form_email_placeholder")}
                        className="mt-1 h-11"
                        aria-invalid={!!emailErr}
                        aria-describedby={emailErr ? "ea-email-err" : undefined}
                        data-testid="early-access-email"
                      />
                      {emailErr && (
                        <p id="ea-email-err" className="mt-1 text-xs text-destructive" data-testid="early-access-email-error">{emailErr}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="ea-first-name" className="text-xs">{t("early_access.form_name_label")}</Label>
                      <Input
                        id="ea-first-name"
                        name="first_name"
                        type="text"
                        autoComplete="given-name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder={t("early_access.form_name_placeholder")}
                        className="mt-1 h-11"
                        data-testid="early-access-first-name"
                      />
                    </div>

                    <div className="flex items-start gap-2.5 pt-1">
                      <Checkbox
                        id="ea-consent"
                        checked={consent}
                        onCheckedChange={(v) => setConsent(v === true)}
                        className="mt-0.5"
                        data-testid="early-access-consent"
                      />
                      <Label htmlFor="ea-consent" className="text-xs leading-5 text-muted-foreground font-normal cursor-pointer">
                        {t("early_access.form_consent_label")}
                        <br />
                        <span className="text-muted-foreground/80">
                          {t("early_access.form_consent_policy_prefix")}{" "}
                          <Link to={privacyHref} className="underline hover:text-foreground" data-testid="early-access-privacy-link">
                            {t("early_access.form_consent_policy_link")}
                          </Link>.
                        </span>
                      </Label>
                    </div>
                    {consentErr && (
                      <p className="text-xs text-destructive" data-testid="early-access-consent-error">{consentErr}</p>
                    )}

                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full h-11"
                      data-testid="early-access-submit"
                    >
                      {submitting ? t("early_access.form_submitting") : t("early_access.form_submit")}
                      {!submitting && <ArrowRight className="w-4 h-4 ml-1.5" />}
                    </Button>
                    {genericErr && (
                      <p className="text-xs text-destructive" data-testid="early-access-generic-error">{genericErr}</p>
                    )}

                    <div className="rounded-lg border border-amber-200/60 dark:border-amber-500/20 bg-amber-50/60 dark:bg-amber-500/5 p-3">
                      <p className="text-xs text-foreground/90" data-testid="early-access-founding-offer">
                        {t("early_access.offer_text")}
                      </p>
                      <p className="mt-1 text-[11px] text-muted-foreground">{t("early_access.offer_fine_print")}</p>
                    </div>
                  </form>
                </>
              )}
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground leading-relaxed px-1" data-testid="early-access-disclaimer">
              {t("early_access.disclaimer")}
            </p>
          </motion.div>
        </section>
      </main>

      <footer className="border-t border-border/60 py-5 text-[11px] text-muted-foreground">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-3">
          <div>© {new Date().getFullYear()} Stockaily</div>
          <nav className="flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="early-access-footer-nav">
            <Link to={privacyHref} className="hover:text-foreground" data-testid="early-access-footer-privacy">{t("early_access.legal_privacy")}</Link>
            <Link to={imprintHref} className="hover:text-foreground" data-testid="early-access-footer-imprint">{t("early_access.legal_imprint")}</Link>
            <Link to={disclaimerHref} className="hover:text-foreground" data-testid="early-access-footer-disclaimer">{t("early_access.legal_disclaimer")}</Link>
            <Link to={methodologyHref} className="hover:text-foreground" data-testid="early-access-footer-methodology">{t("early_access.legal_methodology")}</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}

function BenefitCard({ icon, title, body, testId }) {
  return (
    <li className="rounded-xl border border-border/70 bg-card/50 p-4" data-testid={testId}>
      <div className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-border/70 bg-background text-foreground mb-2">
        {icon}
      </div>
      <div className="text-sm font-semibold tracking-tight">{title}</div>
      <p className="mt-1 text-xs text-muted-foreground leading-snug">{body}</p>
    </li>
  );
}

function PreviewTile({ label, value, tone }) {
  const toneClass = {
    emerald: "text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
    sky: "text-sky-600 dark:text-sky-400 border-sky-500/20",
    amber: "text-amber-600 dark:text-amber-400 border-amber-500/20",
  }[tone] || "";
  return (
    <div className={`w-[82%] flex-none snap-start rounded-lg border bg-background/60 p-3 sm:w-auto sm:min-w-0 ${toneClass}`}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-base sm:text-xl font-semibold leading-snug break-words">{value}</div>
    </div>
  );
}

function SuccessState({ t }) {
  return (
    <div className="text-center py-4" data-testid="early-access-success">
      <div className="mx-auto inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 mb-3">
        <Mail className="w-5 h-5" />
      </div>
      <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight">
        {t("early_access.form_success_title")}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">
        {t("early_access.form_success_body")}
      </p>
    </div>
  );
}

function ConfirmedState({ alreadyConfirmed, t }) {
  return (
    <div className="text-center py-4" data-testid="early-access-confirmed">
      <div className="mx-auto inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 mb-3">
        <CheckCircle2 className="w-6 h-6" />
      </div>
      <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight" data-testid="early-access-confirmed-title">
        {alreadyConfirmed
          ? t("early_access.confirmation_already_title")
          : t("early_access.confirmation_title")}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground" data-testid="early-access-confirmed-body">
        {alreadyConfirmed ? t("early_access.confirmation_already") : t("early_access.confirmation_success")}
      </p>
    </div>
  );
}
